/* ---------------------------------------------------------------------------
 * UC SIZE GUIDE — GENERATED by uc-size-guide/tools/build-theme-bundle.mjs
 *
 * DO NOT EDIT IN THE THEME. Edit uc-size-guide/src/*.js and re-run the build;
 * a change made here is lost the next time anyone runs it.
 *
 * Built 2026-09-21T08:58:13.667Z
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

/* ── src/core.js ───────────────────────────── */
/* --------------------------------------------------------------------------
 * UC SIZE GUIDE — core
 *
 * Every decision the guide makes about numbers, with no DOM in sight. The
 * renderer calls these; the tests call these. Keeping them apart is what makes
 * the axis and sort rules checkable without a browser, which is how the
 * shipping selector and the auto-gift engine were verified too.
 *
 * THE UNIT OF THE LIST IS A SIZE, NOT A PRODUCT. `The Knot` in Large and
 * `The Knot` in Small are two rows, because they are two different objects to
 * buy and a customer choosing between them is choosing between sizes at least
 * as often as between products. An earlier build drew one row per product with
 * a range bar spanning its sizes; it was honest about the span but you could
 * not read one size off it, rank sizes against each other, or compare a Large
 * of one against a Medium of another.
 *
 * All stored values are CENTIMETRES. Inches exist only at the moment of
 * printing a label — see `format`. There is no second stored field and no
 * second copy of the data, because two copies drift.
 * ----------------------------------------------------------------------- */

const CM_PER_INCH = 2.54;

/** The two things a customer sorts and scales by. */
const METRICS = {
  insertable: {
    id: 'insertable',
    label: 'Insertable Length',
    short: 'Insertable',
  },
  circumference: {
    id: 'circumference',
    label: 'Max Circumference',
    short: 'Circumference',
  },
};

const OTHER_METRIC = { insertable: 'circumference', circumference: 'insertable' };

/**
 * Circumferences that are real measurements but must not win "max".
 *
 * The base is the flare or suction cup. It is the widest part of almost
 * everything — it won on 188 of 226 rows — and it is the one part that never
 * goes anywhere near the customer. Letting it win meant "Max Circumference"
 * ranked the catalogue by the size of its bases, which is not a question
 * anyone is asking, and buried the knot and shaft figures that decide whether
 * a toy fits.
 *
 * It is EXCLUDED FROM THE MAX, not hidden: the expanded row and the compare
 * table still print it, because how wide the base sits against a chair or a
 * harness is a real thing to want to know. No row loses its circumference
 * entirely by this — checked against the built set, and asserted in the tests.
 */
const EXCLUDED_FROM_MAX_CIRCUMFERENCE = new Set(['base_circumference']);

/**
 * Measurements that are shown but never drive a headline figure.
 *
 * Two different reasons, one label. `base_circumference` is skipped by
 * `maxCircumference` above. `total_length` is never the length figure
 * either — the length metric is INSERTABLE length, because total length
 * includes a base that does not go anywhere — but nothing excludes it,
 * since nothing ever looks it up.
 *
 * Flagging both is what stops a reader doing the arithmetic themselves and
 * concluding the numbers disagree: the 78cm on the row and the 73.4cm in
 * the bar are both right, and this says which one the bar is.
 */
const NOT_COUNTED_IN_HEADLINE = new Set([
  ...EXCLUDED_FROM_MAX_CIRCUMFERENCE,
  'total_length',
]);

/* ── Per-size derived values ─────────────────────────────────────────────── */

/**
 * The widest circumference on one size, and the point that produced it.
 *
 * The key NAME is never pattern-matched — `category` decides what counts, so a
 * new point (a second knot, a ridge) starts counting the day it is measured.
 * `shaft_below_knot_circumference` and `cock_ring_diameter_stretches_to` both
 * defeat the obvious regex, in opposite directions.
 */
function maxCircumference(sizeValues, measurementMeta) {
  let best = null;
  for (const [key, value] of Object.entries(sizeValues)) {
    const meta = measurementMeta[key];
    if (!meta || meta.category !== 'circumference') continue;
    if (EXCLUDED_FROM_MAX_CIRCUMFERENCE.has(key)) continue;
    if (value == null) continue;
    if (!best || value > best.value) best = { key, value, label: meta.label };
  }
  return best;
}

/** What one size is worth on the active metric, or null. */
function metricValue(sizeValues, metricId, measurementMeta) {
  if (metricId === 'insertable') {
    const v = sizeValues.insertable_length;
    return v == null ? null : { value: v, key: 'insertable_length', label: 'Insertable Length' };
  }
  return maxCircumference(sizeValues, measurementMeta);
}

/* ── Rows ────────────────────────────────────────────────────────────────── */

/**
 * A row's stable identity, used for expand state and compare selection.
 *
 * IT TRAVELS THROUGH AN HTML ATTRIBUTE AND MUST SURVIVE THE ROUND TRIP. This
 * was `handle + " " + size`, and the HTML parser replaces U+0000 in an
 * attribute value with U+FFFD — so every key the DOM handed back differed
 * from the key the code recomputed, by one invisible character. Expand and
 * compare both updated their state correctly and then matched no row.
 *
 * Percent-encoding both halves fixes it twice over: every character that
 * comes out is one HTML leaves alone, and `|` is itself encoded inside each
 * half, so the separator cannot be ambiguous however a size is titled.
 */
function rowKey(handle, sizeTitle) {
  return `${encodeURIComponent(handle)}|${encodeURIComponent(sizeTitle)}`;
}

/**
 * Characters a browser will hand back unchanged from an HTML attribute.
 * Exported so the test can assert the property rather than the format — the
 * separator may change again; surviving the round trip may not.
 */
const ATTRIBUTE_SAFE = /^[A-Za-z0-9\-_.!~*'()%|]+$/;

/**
 * Flatten the catalogue into one row per product-size.
 *
 * `value` drives the bar's width and the sort; `other` is the second figure
 * printed beside it, so both dimensions stay on screen. A size missing
 * the active metric is dropped rather than rendered as a blank bar — on the
 * built set that never happens for insertable, which is the inclusion rule.
 */
function buildRows(products, metricId, measurementMeta) {
  const other = OTHER_METRIC[metricId];
  const rows = [];
  for (const product of products) {
    for (const size of product.sizes) {
      const hit = metricValue(size.m, metricId, measurementMeta);
      if (!hit) continue;
      const otherHit = metricValue(size.m, other, measurementMeta);
      rows.push({
        key: rowKey(product.h, size.s),
        product,
        size,
        value: hit.value,
        at: hit,
        other: otherHit ? otherHit.value : null,
        otherAt: otherHit,
      });
    }
  }
  return rows;
}

/* ── Axis ────────────────────────────────────────────────────────────────── */

/**
 * The value the bars are drawn against: whatever fits the rows ON SCREEN.
 *
 * The brief asked for an axis that never moves, so a bar would mean the same
 * thing before and after a filter. Two builds tried to honour that and both
 * failed the same way. Scaling to the true maximum draws everything ordinary
 * as a stub, because five depth trainers reach 73.4cm against a 90th
 * percentile of 24.9cm. Pinning a 30cm ceiling and clipping past it looked
 * fine until the list was capped at 25 rows — sorted largest-first, that put
 * the reader squarely in the tail, and 12 of the first 25 bars ran off the
 * end and became indistinguishable from each other.
 *
 * So the axis fits the rendered rows. Nothing clips, the giants are
 * comparable against each other, and a filtered set is drawn at a scale that
 * uses the width available. The cost is real and deliberate: bars rescale
 * when the filter, the sort or "Show all" changes what is on screen. The
 * ruler is relabelled at the same moment and every row prints its own figure,
 * so the change is visible rather than silent — which is what made the fixed
 * axis worth abandoning, since a clipped bar hid its size completely.
 *
 * Pass the values actually being RENDERED, not the whole filtered set.
 */
function axisMax(renderedValues) {
  let max = 0;
  for (const v of renderedValues) if (v != null && v > max) max = v;
  // The biggest row on screen IS the axis, so its bar fills the track and
  // every other bar is read as a fraction of it. Rounding up to a tidy number
  // for the ruler's sake left the largest bar at ~92% of the width, which
  // wastes the one length a reader can measure everything else against.
  //
  // A floor so one small row does not get a 1.8cm axis, which would draw a
  // 1.8cm product full width and read as enormous.
  return Math.max(AXIS_MIN, max);
}

/** Smallest axis we will ever draw, cm. */
const AXIS_MIN = 10;

/** Round up to a readable tick boundary: 5s below 50, 10s above. */
function niceCeiling(n) {
  const step = n <= 50 ? 5 : 10;
  return Math.ceil(n / step) * step;
}

/**
 * Tick positions for the ruler, in the DISPLAY unit.
 *
 * The axis is now whatever the biggest row measures, so it is rarely a round
 * number. Ticks still fall on round intervals — nobody reads a ruler marked
 * 0, 12.2, 24.5 — and the axis value is added as a final labelled tick at the
 * right edge, which is where the longest bar ends. The last interval tick is
 * dropped when it would collide with it.
 */
function ticks(axisMaxCm, unit) {
  const max = unit === 'in' ? axisMaxCm / CM_PER_INCH : axisMaxCm;
  if (!(max > 0)) return [{ value: 0, pct: 0 }];

  // Aim for 5-7 labelled ticks at phone width; more than that and the labels
  // collide, fewer and the axis stops being readable as a scale.
  const step = [1, 2, 2.5, 5, 10, 20, 25, 50].find((s) => s >= max / 6) || 100;

  const out = [];
  for (let v = 0; v < max - 1e-9; v += step) out.push(round1(v));

  // The axis value is the final label and it is anchored to the right edge,
  // so the interval tick before it needs room. An axis of 73.4 with a step of
  // 20 left the 60 tick 13.4 away and the two rendered as "6073.4" on a 125px
  // track. Drop it when the gap is under nine tenths of a step — which still
  // keeps every tick on a round axis, where the end lands a full step clear
  // (60 with a step of 10 keeps its 50).
  const last = out[out.length - 1];
  if (out.length > 1 && max - last < step * 0.9) out.pop();

  return [...out.map((v) => ({ value: v, pct: (v / max) * 100 })), { value: round1(max), pct: 100 }];
}

function round1(n) {
  return Math.round(n * 10) / 10;
}

/* ── Filter and sort ─────────────────────────────────────────────────────── */

/**
 * Split a query into terms. Every term must match, so "knot large" finds the
 * Large sizes of knotted products rather than everything called either.
 */
function parseQuery(query) {
  return String(query || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * What a row is searchable by: its product, its size, and the collections it
 * sits in.
 *
 * Collection LABELS, not handles — someone types "dragon", not
 * "dragon-dildos", and the handle would also match on stray hyphens. The
 * size is in there because "knot large" is a natural thing to type and the
 * row is a product-size in the first place.
 */
function searchHaystack(row, collectionLabels = {}) {
  // Cached on the row. Typing rebuilt this for all 226 rows on every
  // keystroke — the same concat and `toLowerCase` over and over for a
  // string that cannot change. Keyed on the labels object so a different
  // dataset cannot read a stale one.
  if (row.__hay !== undefined && row.__hayFor === collectionLabels) return row.__hay;

  const collections = (row.product.c || []).map((h) => collectionLabels[h] || h);
  row.__hay = `${row.product.t} ${row.size.s} ${collections.join(' ')}`.toLowerCase();
  row.__hayFor = collectionLabels;
  return row.__hay;
}

function searchRows(rows, query, collectionLabels) {
  const terms = parseQuery(query);
  if (!terms.length) return rows;
  return rows.filter((row) => {
    const hay = searchHaystack(row, collectionLabels);
    return terms.every((t) => hay.includes(t));
  });
}

function filterRows(rows, collection) {
  if (!collection || collection === 'all') return rows;
  return rows.filter((r) => r.product.c.includes(collection));
}

/** Reading order for a product's sizes, used to break sort ties sensibly. */
const SIZE_ORDER = [
  'mini', 'xx-small', 'x-small', 'xsmall', 'small', 'medium',
  'large', 'x-large', 'xlarge', 'xx-large', 'one size', 'default title',
];

function sizeRank(title) {
  const i = SIZE_ORDER.indexOf(String(title).trim().toLowerCase());
  return i === -1 ? SIZE_ORDER.length : i;
}

/**
 * Sort by the active metric. Largest-first is the default, because that is
 * what people are looking for.
 *
 * Ties fall back to product title then size order, so the four sizes of one
 * product never interleave arbitrarily when they happen to measure the same.
 */
function sortRows(rows, direction) {
  const desc = direction !== 'asc';
  return [...rows].sort((a, b) => {
    if (a.value !== b.value) return desc ? b.value - a.value : a.value - b.value;
    return (
      a.product.t.localeCompare(b.product.t) ||
      sizeRank(a.size.s) - sizeRank(b.size.s)
    );
  });
}

/* ── Bar geometry ────────────────────────────────────────────────────────── */

/**
 * A bar runs from zero to its value.
 *
 * The clamp is defensive only: `axisMax` is computed from the rows being
 * rendered, so nothing can exceed it. It stays because a future change that
 * caps the axis again would otherwise let bars silently saturate at full
 * width and read as equal — which is exactly the failure that retired the
 * fixed axis.
 */
function barGeometry(value, axis) {
  if (value == null) return null;
  return { widthPct: Math.max(Math.min(100, (value / axis) * 100), 1) };
}

/*
 * BAR THICKNESS USED TO ENCODE THE OTHER METRIC, and it is gone.
 *
 * The idea was that both dimensions stay on screen whichever one is driving
 * the width. In practice it was a second visual encoding with no axis, no
 * legend and no label, sitting next to the same figure written out in words —
 * so the only way to read it was to be told what it meant. Removed rather
 * than explained: the number beside the bar already answers it.
 *
 * If an at-a-glance girth comparison is wanted later, a second labelled bar
 * is the honest way to do it, not a thickness nobody can decode.
 */

/* ── Display ─────────────────────────────────────────────────────────────── */

/** cm in, chosen unit out. Display only — nothing converted is ever stored. */
function format(valueCm, unit, { decimals } = {}) {
  if (valueCm == null) return '—';
  if (unit === 'in') return `${round(valueCm / CM_PER_INCH, decimals ?? 1)}″`;
  return `${round(valueCm, decimals ?? 1)}cm`;
}

function round(n, dp) {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

/* ── Compare ─────────────────────────────────────────────────────────────── */

const MAX_COMPARE = 3;

/**
 * How many rows the list shows before "Show all".
 *
 * Not a download cost — the whole catalogue is one 66KB file that has already
 * arrived, and it has to, because the axis is computed across the full set. It is a RENDER cost: 226 rows is 320KB of
 * HTML and 4,500 DOM nodes, rebuilt from scratch on every tap, which measured
 * 30ms on a desktop and several times that on a phone. Capping the first
 * paint makes every interaction cheap and, separately, makes the list
 * scannable — 226 rows in one wall is not a thing anyone reads.
 */
const PAGE_SIZE = 25;

/**
 * Toggle a row in the compare selection.
 *
 * Returns the new selection plus whether the cap turned the click away, so the
 * renderer can say so. Silently ignoring a fourth tap reads as a broken
 * checkbox.
 */
function toggleCompare(selection, key) {
  if (selection.includes(key)) {
    return { selection: selection.filter((k) => k !== key), rejected: false };
  }
  if (selection.length >= MAX_COMPARE) return { selection, rejected: true };
  return { selection: [...selection, key], rejected: false };
}

/**
 * The rows of the compare table: every dimension any selected size carries, in
 * PourHub's own sort order, whichever metric is on screen. The bars answer
 * "which is bigger"; this answers "by how much, on the measurement I care
 * about", which is the question the bars cannot.
 */
function compareTable(sizeValueSets, measurementMeta) {
  const keys = new Set();
  for (const m of sizeValueSets) for (const k of Object.keys(m)) keys.add(k);
  return [...keys]
    .filter((k) => measurementMeta[k])
    .sort((a, b) => measurementMeta[a].sort - measurementMeta[b].sort || a.localeCompare(b))
    .map((key) => ({
      key,
      label: measurementMeta[key].label,
      category: measurementMeta[key].category,
      /** True for a measurement no headline figure is ever taken from. */
      excludedFromMax: NOT_COUNTED_IN_HEADLINE.has(key),
    }));
}


/* ── src/element.js ───────────────────────────── */
/* --------------------------------------------------------------------------
 * UC SIZE GUIDE — <uc-size-guide>
 *
 * The renderer. Every number it prints comes from src/core.js, which is tested
 * in node; this file is the DOM around those numbers and nothing else. When
 * something here looks like a calculation, it belongs in core.
 *
 *   <uc-size-guide data-src="{{ 'uc-size-guide-data.json' | asset_url }}">
 *     <script type="application/json" data-ucsg-data>{ ...catalogue... }</script>
 *
 * Two sources, in that order of preference. See `load` for why.
 *
 * One row per product-SIZE. See the header of core.js for why.
 * ----------------------------------------------------------------------- */


// Per-viewer conveniences only. Everything that matters is in the data; if
// storage throws (private window, blocked site data) the guide must still open
// on its defaults rather than not open at all.
const STORE_KEY = 'uc-size-guide:prefs';

/**
 * Room left for the theme's sticky header when scrolling the guide into
 * view. Impact's is ~60px and grows with the announcement bar; overshooting
 * a little shows a sliver of what is above, which reads as "here" rather
 * than as a jump.
 */
const STICKY_HEADER_ALLOWANCE = 80;

function readPrefs() {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY)) || {};
  } catch {
    return {};
  }
}

function writePrefs(patch) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({ ...readPrefs(), ...patch }));
  } catch {
    /* no-op */
  }
}

const esc = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/**
 * Shopify's CDN resizes on `width`, and a thumbnail asked for at full size is
 * the single heaviest thing on this page. 192px covers the 64px slot at 3x and
 * the 84px desktop slot at 2x. `&width=` is safe on any `cdn.shopify.com` URL;
 * anything else is left alone rather than guessed at.
 */
function thumb(url, px) {
  if (!url || !/cdn\.shopify\.com/.test(url)) return url;
  return `${url}${url.includes('?') ? '&' : '?'}width=${px}`;
}

function initials(title) {
  return String(title)
    .replace(/^the\s+/i, '')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase();
}

/** "One Size" and "Default Title" are Shopify defaults, not a size to print. */
function sizeLabel(title) {
  const t = String(title).trim();
  return /^(default title)$/i.test(t) ? '' : t;
}

let instanceCount = 0;

class UcSizeGuide extends HTMLElement {
  connectedCallback() {
    if (this.dataset.booted === '1') return;
    this.dataset.booted = '1';

    // `<label for>` needs an id, and the quick-view drawer can put a second
    // guide in the same document — two elements sharing an id would point
    // every label at the first one's select.
    this.uid = `ucsg-${++instanceCount}`;

    const prefs = readPrefs();
    this.state = {
      unit: prefs.unit === 'in' ? 'in' : 'cm',
      collection: 'all',
      query: '',
      metric: METRICS[prefs.metric] ? prefs.metric : 'insertable',
      sort: prefs.sort === 'asc' ? 'asc' : 'desc',
      expanded: new Set(),
      // How many rows are on screen. Reset whenever the list itself changes,
      // because "show all" of one filter should not silently mean "show all"
      // of the next one the customer picks.
      limit: PAGE_SIZE,
      compare: [],
      comparing: false,
      data: null,
      error: null,
    };

    this.addEventListener('click', this.onClick);
    // A <select> fires `change`, never `click`, so the delegated click
    // handler alone would leave the collection filter inert.
    this.addEventListener('change', this.onChange);
    // `input`, not `change`: the list has to narrow as you type, and a text
    // field only fires `change` when it loses focus.
    this.addEventListener('input', this.onInput);

    // Compare is a second screen, and on a phone the system Back gesture is
    // how you leave one. Without a history entry it left the whole PAGE
    // instead, losing the selection and the filter with it.
    if (typeof window !== 'undefined') window.addEventListener('popstate', this.onPopState);

    this.load();
  }

  /**
   * The catalogue, preferring the shop metafield over the built asset.
   *
   * The section inlines `shop.metafields.uc_size_guide.catalogue` when the
   * UncoverOps sync has populated it. That copy is as fresh as the last sync
   * run; the asset is only as fresh as the last theme push, so a product
   * measured this morning is in one and not the other — which is the whole
   * reason the sync exists.
   *
   * The asset is NOT removed when the metafield is present. If the sync
   * breaks, writes a shapeless payload, or the metafield quietly loses
   * Storefront API access and reads as unset in Liquid, the guide falls
   * through and renders the last built catalogue. Stale numbers beat an
   * empty box where a size guide should be.
   */
  async load() {
    const inline = this.readInlineData();
    if (inline) {
      this.state.data = inline;
      this.render();
      return;
    }

    try {
      const res = await fetch(this.dataset.src, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      this.state.data = await res.json();
    } catch (err) {
      // A guide that renders an empty page looks like a broken theme. Say what
      // happened and keep a link that still works.
      this.state.error = err.message;
    }
    this.render();
  }

  /**
   * The inlined metafield, or null if there is nothing usable there.
   *
   * Returns null rather than throwing on every failure mode, because each
   * one has the same right answer: ignore it and let the asset load. A sync
   * that ran and wrote an empty catalogue is treated as absent too — an
   * empty guide is indistinguishable from a broken one to a customer, and
   * yesterday's 67 products are worth more than today's nothing.
   */
  readInlineData() {
    const node = this.querySelector ? this.querySelector('script[data-ucsg-data]') : null;
    if (!node) return null;
    try {
      const parsed = JSON.parse(node.textContent || '');
      if (parsed && Array.isArray(parsed.products) && parsed.products.length) return parsed;
    } catch {
      /* malformed JSON in the metafield — the asset is the fallback */
    }
    return null;
  }

  /* ── derivation ───────────────────────────────────────────────────── */

  /**
   * Rows for a metric, built once.
   *
   * `buildRows` walks every product and size and allocates a row object for
   * each. It ran on every render — twice while comparing, since
   * `selectedRows` called it again — for a result that only changes when
   * the metric or the dataset does. Typing a letter re-derived 226 rows
   * that were identical to the 226 from the keystroke before.
   */
  rowsFor(metric) {
    // `state.data` is treated as IMMUTABLE: the cache is keyed on its
    // identity, and a row's key is computed from the product handle and the
    // size title at build time. Mutating a loaded dataset in place leaves
    // stale keys behind — expand and compare then match nothing, silently.
    // Replace the object instead.
    const { data } = this.state;
    const c = this.rowCache;
    if (c && c.data === data && c.metric === metric) return c.rows;
    const rows = buildRows(data.products, metric, data.measurements);
    this.rowCache = { data, metric, rows };
    return rows;
  }

  derive() {
    const { data, metric, collection, sort } = this.state;

    const all = this.rowsFor(metric);
    const matching = searchRows(
      filterRows(all, collection),
      this.state.query,
      data.collections,
    );
    const rows = sortRows(matching, sort);

    // The slice happens HERE, not in renderList, because the axis is computed
    // from the rows actually rendered — see `axisMax`. Splitting the two
    // would let the scale describe rows that are not on screen.
    const shown = rows.slice(0, this.state.limit);

    return { rows, shown, axis: axisMax(shown.map((r) => r.value)), allRows: all, total: all.length };
  }

  /** The selected rows, in selection order, ignoring any that no longer exist. */
  selectedRows() {
    const { compare, metric } = this.state;
    const byKey = new Map(this.rowsFor(metric).map((r) => [r.key, r]));
    return compare.map((k) => byKey.get(k)).filter(Boolean);
  }

  /* ── render ───────────────────────────────────────────────────────── */

  render() {
    if (this.state.error) {
      this.innerHTML = `<div class="ucsg ucsg--error" role="alert">
        <p>The size guide could not load right now.</p>
        <p class="ucsg__muted">${esc(this.state.error)}</p>
      </div>`;
      return;
    }
    if (!this.state.data) {
      this.innerHTML = `<div class="ucsg ucsg--loading" aria-busy="true">Loading sizes…</div>`;
      return;
    }

    const { rows, shown, axis, allRows } = this.derive();
    this.innerHTML = `<div class="ucsg">
      ${this.renderSearch()}
      ${this.renderCollectionPicker(allRows)}
      ${this.renderControls()}
      ${this.state.comparing ? this.renderCompare() : this.renderList(rows, shown, axis)}
    </div>
    ${this.renderFooter()}`;
  }

  /**
   * One capsule split in two, matching the Inch/Cm control on the existing
   * product-page size guide. `Cm` leads because the data is stored in
   * centimetres and that is the default; the PDP's own control leads with
   * Inch, which is the one inconsistency between them.
   */
  renderUnits() {
    const { unit } = this.state;
    return `<div class="ucsg__units" role="group" aria-label="Units">
      ${[['cm', 'Cm'], ['in', 'Inch']].map(([u, label]) => `
        <button type="button" class="ucsg__unit${unit === u ? ' is-active' : ''}"
                data-act="unit" data-val="${u}" aria-pressed="${unit === u}">${label}</button>`).join('')}
    </div>`;
  }

  renderSearch() {
    const q = this.state.query;
    return `<div class="ucsg__search">
      <input type="search" class="ucsg__searchinput" data-act="search"
             value="${esc(q)}" placeholder="Search by name, size or collection"
             aria-label="Search sizes" autocomplete="off" enterkeyhint="search">
      ${q
        ? `<button type="button" class="ucsg__searchclear" data-act="clearsearch"
                   aria-label="Clear search">&times;</button>`
        : ''}
    </div>`;
  }

  renderCollectionPicker(allRows) {
    const { data, collection } = this.state;

    // Counts are of ROWS, not products — the list is one row per size, so
    // "19 products" beside a filter that yields 62 lines would be wrong.
    const count = (handle) => filterRows(allRows, handle).length;

    const options = [
      ['all', `All sizes (${allRows.length})`],
      ...Object.entries(data.collections).map(([h, label]) => [h, `${label} (${count(h)})`]),
    ];

    // No visible "Collection" label — the options name themselves ("All
    // sizes", "Knot Dildos") so the word only repeated what the control
    // already said, and it cost the width the units now use. The select
    // still carries the name for anyone who cannot see the options.
    return `<div class="ucsg__filter">
      <div class="ucsg__selectwrap">
        <select class="ucsg__select" id="${this.uid}-collection" data-act="collection"
                aria-label="Filter by collection">
          ${options.map(([handle, label]) => `
            <option value="${esc(handle)}"${collection === handle ? ' selected' : ''}>${esc(label)}</option>`).join('')}
        </select>
      </div>
      ${this.renderUnits()}
    </div>`;
  }

  renderControls() {
    const { metric, sort } = this.state;
    const seg = (act, current, options) => `
      <div class="ucsg__seg" role="group">
        ${options.map(([val, label]) => `
          <button type="button" class="ucsg__segbtn${current === val ? ' is-active' : ''}"
                  data-act="${act}" data-val="${val}" aria-pressed="${current === val}">${esc(label)}</button>`).join('')}
      </div>`;

    return `<div class="ucsg__controls">
      ${seg('metric', metric, [['insertable', METRICS.insertable.label], ['circumference', METRICS.circumference.label]])}
      ${seg('sort', sort, [['desc', 'Largest first'], ['asc', 'Smallest first']])}
    </div>`;
  }

  renderRuler(axis) {
    const { unit } = this.state;
    const marks = ticks(axis, unit);
    return `<div class="ucsg__ruler" aria-hidden="true">
      ${marks.map((t, i) => `
        <span class="ucsg__tick${i === marks.length - 1 ? ' is-end' : ''}" style="left:${t.pct}%"><i></i><b>${t.value}</b></span>`).join('')}
      <span class="ucsg__rulerunit">${unit}</span>
    </div>`;
  }

  renderList(rows, shown, axis) {
    if (!rows.length) {
      return `<p class="ucsg__empty" role="status">${
        this.state.query
          ? `Nothing matches &ldquo;${esc(this.state.query)}&rdquo;.`
          : 'Nothing in that collection has been measured yet.'
      }</p>`;
    }

    const hidden = rows.length - shown.length;

    // The count states WHERE YOU ARE and the button states what it does.
    // They used to say the same thing three times over — "Show all 226 sizes
    // (201 more)" above "Showing 25 of 226 sizes" — which is three numbers
    // for one fact, and the reader has to reconcile them to be sure.
    const filtering = this.state.collection !== 'all' || this.state.query !== '';

    return `
      <div class="ucsg__hintrow">
        <p class="ucsg__hint">Tap any row for its full measurements.</p>
        ${filtering
          ? `<button type="button" class="ucsg__clearfilters" data-act="clearfilters">
               Clear filters <span aria-hidden="true">&times;</span>
             </button>`
          : ''}
      </div>
      ${this.renderRuler(axis)}
      <ul class="ucsg__list">${shown.map((r) => this.renderRow(r, axis)).join('')}</ul>
      ${hidden > 0
        ? `<div class="ucsg__loadmore">
             <p class="ucsg__progresstext" role="status">
               You&rsquo;ve viewed <b>${shown.length}</b> of <b>${rows.length}</b> sizes
             </p>
             <div class="ucsg__progress" role="progressbar"
                  aria-valuenow="${shown.length}" aria-valuemin="0" aria-valuemax="${rows.length}"
                  aria-label="Sizes shown">
               <span style="width:${((shown.length / rows.length) * 100).toFixed(1)}%"></span>
             </div>
             <button type="button" class="ucsg__more" data-act="more">Load More</button>
           </div>`
        : `<p class="ucsg__count" role="status">${rows.length} size${rows.length === 1 ? '' : 's'}</p>`}`;
  }

  renderRow(row, axis, { inCompare = false, index = 0 } = {}) {
    const { unit, metric, data, expanded, compare } = this.state;
    const { product, size } = row;
    const geo = barGeometry(row.value, axis);
    const open = expanded.has(row.key);
    const checked = compare.includes(row.key);
    const disabled = !checked && compare.length >= MAX_COMPARE;
    const sz = sizeLabel(size.s);

    const tags = product.c
      .filter((h) => data.collections[h])
      // Three, not two: the size moved to its own line above, so this row
      // has the whole width to itself.
      .slice(0, 3)
      .map((h) => `<span class="ucsg__tag">${esc(data.collections[h])}</span>`)
      .join('');

    return `<li class="ucsg__row${open ? ' is-open' : ''}" data-key="${esc(row.key)}" data-handle="${esc(product.h)}">
      <div class="ucsg__main">
        ${inCompare
          ? `<span class="ucsg__num ucsg__num--row" aria-hidden="true">${index}</span>`
          : `
        <label class="ucsg__check${disabled ? ' is-disabled' : ''}">
          <input type="checkbox" data-act="compare" data-val="${esc(row.key)}"
                 ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''}
                 aria-label="Compare ${esc(product.t)}${sz ? `, ${esc(sz)}` : ''}">
          <span aria-hidden="true"></span>
        </label>`}

        ${product.img
          ? `<img class="ucsg__thumb" src="${esc(thumb(product.img, 192))}" alt="" loading="lazy" width="64" height="64">`
          : `<span class="ucsg__thumb ucsg__thumb--none" aria-hidden="true">${esc(initials(product.t))}</span>`}

        <button type="button" class="ucsg__body" data-act="expand" data-val="${esc(row.key)}"
                aria-expanded="${open}"
                aria-label="${open ? 'Hide' : 'Show'} all measurements for ${esc(product.t)}${sz ? `, ${esc(sz)}` : ''}">
          <span class="ucsg__nameline">
            <span class="ucsg__name">${esc(product.t)}</span>
            <span class="ucsg__chev" aria-hidden="true"></span>
          </span>
          ${sz ? `<span class="ucsg__sizeline"><span class="ucsg__size">${esc(sz)}</span></span>` : ''}
          <span class="ucsg__tags">${tags}</span>
          <span class="ucsg__barline">
            <span class="ucsg__track"><span class="ucsg__lane">
              ${geo ? `<span class="ucsg__bar" style="width:${geo.widthPct}%"></span>` : ''}
            </span></span>
            <span class="ucsg__nums">
              <b>${esc(format(row.value, unit))} <em>${esc(METRICS[metric].short.toLowerCase())}</em></b>
              <i>${esc(format(row.other, unit))} <em>${esc(METRICS[OTHER_METRIC[metric]].short.toLowerCase())}</em></i>
            </span>
          </span>
        </button>
      </div>
      ${open ? this.renderDetail(row) : ''}
    </li>`;
  }

  renderDetail(row) {
    const { unit, data } = this.state;
    const meta = data.measurements;
    const widest = maxCircumference(row.size.m, meta);

    return `<div class="ucsg__detail">
      <table class="ucsg__table">
        <tbody>
          ${compareTable([row.size.m], meta).map((k) => {
            const isMax = widest && widest.key === k.key;
            return `<tr>
              <th scope="row">${esc(k.label)}${
                k.excludedFromMax ? ' <span class="ucsg__hint">(not counted in max)</span>' : ''
              }</th>
              <td class="${isMax ? 'is-widest' : ''}">${esc(format(row.size.m[k.key], unit))}</td>
            </tr>`;
          }).join('')}
        </tbody>
      </table>
      <a class="ucsg__viewbtn" href="/products/${esc(row.product.h)}">View ${esc(row.product.t)}</a>
    </div>`;
  }

  /* ── compare view ─────────────────────────────────────────────────── */

  renderCompare() {
    const { unit, data } = this.state;
    const meta = data.measurements;
    const picked = this.selectedRows();
    // The comparison is between these rows and nothing else, so the scale is
    // theirs — the same rule the list follows.
    const axis = axisMax(picked.map((r) => r.value));
    const keys = compareTable(picked.map((r) => r.size.m), meta);

    return `<div class="ucsg__compare">
      <button type="button" class="ucsg__back" data-act="back">← Back to all sizes</button>
      ${this.renderRuler(axis)}
      <ul class="ucsg__list ucsg__list--compare">
        ${picked.map((r, i) => this.renderRow(r, axis, { inCompare: true, index: i + 1 })).join('')}
      </ul>

      <div class="ucsg__scroller">
        <table class="ucsg__table ucsg__table--compare">
          <thead><tr>
            <th scope="col">Measurement</th>
            ${picked.map((r, i) => {
              const sz = sizeLabel(r.size.s);
              const name = `${r.product.t}${sz ? `, ${sz}` : ''}`;
              // The product TITLE used to head each column, and the titles
              // run to "Sinnovator Dagon Depth Trainer Tentacle Dildo" — one
              // of them pushed the numbers off the right of a phone screen.
              // A number and a thumbnail identify the column in ~72px. The
              // number matches the bar rows directly above, which carry the
              // full name — that pairing is the reason it is here, and the
              // reason the separate numbered KEY was dropped: the bars were
              // already saying everything the key repeated. The size goes
              // under the image because two columns are often one product in
              // two sizes, where the picture is identical. The name is here
              // too, for a screen reader, which sees no picture at all.
              return `<th scope="col" class="ucsg__colhead">
                <span class="ucsg__vh">${esc(name)}</span>
                <span class="ucsg__num" aria-hidden="true">${i + 1}</span>
                <a class="ucsg__collink" href="/products/${esc(r.product.h)}" aria-label="View ${esc(name)}">
                  ${r.product.img
                    ? `<img class="ucsg__colthumb" src="${esc(thumb(r.product.img, 128))}" alt="" width="44" height="44">`
                    : `<span class="ucsg__colthumb ucsg__thumb--none" aria-hidden="true">${esc(initials(r.product.t))}</span>`}
                  ${sz ? `<span class="ucsg__colsize" aria-hidden="true">${esc(sz)}</span>` : ''}
                </a>
              </th>`;
            }).join('')}
          </tr></thead>
          <tbody>
            ${keys.map((k) => `
              <tr>
                <th scope="row">${esc(k.label)}${
                  k.excludedFromMax ? ' <span class="ucsg__hint">(not counted in max)</span>' : ''
                }</th>
                ${picked.map((r) => {
                  const v = r.size.m[k.key];
                  if (v == null) return `<td class="ucsg__na">—</td>`;
                  const widest = maxCircumference(r.size.m, meta);
                  const isMax = widest && widest.key === k.key;
                  return `<td class="${isMax ? 'is-widest' : ''}">${esc(format(v, unit))}</td>`;
                }).join('')}
              </tr>`).join('')}
          </tbody>
        </table>
      </div>
      ${this.renderCompareLinks(picked)}
      <button type="button" class="ucsg__back ucsg__back--foot" data-act="back">← Back to all sizes</button>
    </div>`;
  }

  /**
   * A worded link to each product being compared.
   *
   * The thumbnails in the table head are links, but a 40px picture is not an
   * obvious way out — a customer who has decided which one they want needs a
   * plain "View …" to click. Numbered to match the columns and the bars.
   */
  renderCompareLinks(picked) {
    return `<ul class="ucsg__golinks">
      ${picked.map((r, i) => {
        const sz = sizeLabel(r.size.s);
        return `<li>
          <span class="ucsg__num" aria-hidden="true">${i + 1}</span>
          <a href="/products/${esc(r.product.h)}">View ${esc(r.product.t)}${sz ? ` · ${esc(sz)}` : ''} →</a>
        </li>`;
      }).join('')}
    </ul>`;
  }


  renderFooter() {
    const { compare, comparing } = this.state;
    if (!compare.length || comparing) return '';
    return `<div class="ucsg__footer" role="region" aria-label="Compare selection">
      <span>${compare.length} of ${MAX_COMPARE} selected</span>
      <span class="ucsg__footeractions">
        <button type="button" class="ucsg__clear" data-act="clear">Clear</button>
        <button type="button" class="ucsg__go" data-act="go">Compare</button>
      </span>
    </div>`;
  }

  /* ── events ───────────────────────────────────────────────────────── */

  /**
   * Add a history entry for the compare view.
   *
   * Same URL, so nothing is shared or bookmarked differently — the entry
   * exists only so Back has something to pop. `history` is missing in the
   * test harness and can throw in a sandboxed frame, hence the guard.
   */
  pushCompareState() {
    try {
      history.pushState({ ucsgCompare: this.uid }, '');
      this.pushedCompareState = true;
    } catch {
      this.pushedCompareState = false;
    }
  }

  /** Returns true when it popped, meaning `popstate` will do the render. */
  popCompareState() {
    if (!this.pushedCompareState) return false;
    this.pushedCompareState = false;
    try {
      history.back();
      return true;
    } catch {
      return false;
    }
  }

  onPopState = () => {
    if (!this.state || !this.state.comparing) return;
    this.state.comparing = false;
    this.pushedCompareState = false;
    this.render();
    this.scrollAfter = 'restore';
    this.applyScroll();
  };

  /**
   * Typing narrows the list.
   *
   * Re-rendering replaces the input, which drops focus and the caret with
   * it — so the next keystroke would go nowhere and the field would appear
   * to reject every second character. `restoreSearchFocus` puts both back.
   */
  onInput = (event) => {
    const el = event.target.closest ? event.target.closest('[data-act]') : null;
    if (!el || el.dataset.act !== 'search') return;
    this.state.query = el.value;
    this.state.limit = PAGE_SIZE;
    this.caret = el.selectionStart;
    this.render();
    this.restoreSearchFocus();
  };

  restoreSearchFocus() {
    const input = this.querySelector ? this.querySelector('.ucsg__searchinput') : null;
    if (!input || !input.focus) return;
    input.focus();
    if (this.caret != null && input.setSelectionRange) {
      try {
        input.setSelectionRange(this.caret, this.caret);
      } catch {
        /* a type="search" field can refuse this in some browsers */
      }
    }
  }

  /** `<select>` only ever fires `change`. */
  onChange = (event) => {
    const el = event.target.closest('[data-act]');
    if (!el || !this.contains(el) || el.dataset.act !== 'collection') return;
    this.state.collection = el.value;
    this.state.limit = PAGE_SIZE;
    this.render();
  };

  onClick = (event) => {
    const el = event.target.closest('[data-act]');
    if (!el || !this.contains(el)) return;
    const { act, val } = el.dataset;
    const s = this.state;

    switch (act) {
      case 'unit':
        s.unit = val;
        writePrefs({ unit: val });
        break;
      case 'collection':
        // Reached only if something other than the <select> ever sets a
        // collection; the picker itself goes through `onChange`.
        if (val === undefined) return;
        s.collection = val;
        s.limit = PAGE_SIZE;
        break;
      case 'metric':
        s.metric = val;
        s.limit = PAGE_SIZE;
        writePrefs({ metric: val });
        break;
      case 'sort':
        s.sort = val;
        s.limit = PAGE_SIZE;
        writePrefs({ sort: val });
        break;
      case 'more':
        // Everything, in one tap. Stepping by a page meant nine taps to see
        // 226 rows, and the render cost the cap exists for is paid once
        // here rather than avoided — 320KB of HTML is a slow frame, not a
        // reason to make someone tap nine times.
        s.limit = Infinity;
        break;
      case 'expand':
        if (s.expanded.has(val)) s.expanded.delete(val);
        else s.expanded.add(val);
        break;
      case 'compare': {
        const res = toggleCompare(s.compare, val);
        s.compare = res.selection;
        if (res.rejected) {
          // The checkbox is already disabled at the cap, so this only fires
          // via keyboard or a stale DOM. Undo the visual tick either way.
          event.target.checked = false;
          return;
        }
        if (!s.compare.length && s.comparing && this.popCompareState()) return;
        if (!s.compare.length) s.comparing = false;
        break;
      }
      case 'clearsearch':
        s.query = '';
        s.limit = PAGE_SIZE;
        break;
      case 'clearfilters':
        s.query = '';
        s.collection = 'all';
        s.limit = PAGE_SIZE;
        break;
      case 'clear':
        s.compare = [];
        if (s.comparing && this.popCompareState()) return;
        s.comparing = false;
        break;
      case 'go':
        // Any row left open pushes the comparison table down by a whole
        // measurement table, which is the one thing the view exists to
        // show. Collapse them: the compare table says everything an
        // expanded row would, for all three at once.
        s.expanded.clear();
        // Entering compare replaces 25 rows with three, so the page gets
        // much shorter and the browser keeps the scroll offset — you end up
        // staring at the footer, below everything that just changed.
        this.scrollBeforeCompare = this.scrollY();
        s.comparing = true;
        this.pushCompareState();
        this.scrollAfter = 'top';
        break;
      case 'back':
        // Go through history so our own button and the system Back leave by
        // the same door; `popstate` does the re-render. Only fall through to
        // setting state directly if there was no entry to pop.
        if (this.popCompareState()) return;
        s.comparing = false;
        this.scrollAfter = 'restore';
        break;
      default:
        return;
    }

    this.render();
    this.applyScroll();
  };

  scrollY() {
    return typeof window === 'undefined' ? 0 : window.scrollY || 0;
  }

  /**
   * Put the top of the guide under the header after a view change.
   *
   * `scrollIntoView` would tuck it beneath Impact's sticky header, so the
   * offset is measured from the document instead. Leaving compare restores
   * the position you came from, which is the row you were looking at.
   */
  /**
   * Put the top of the guide under the header after a view change.
   *
   * Three things had to be got right here, and each was found on the page
   * rather than in a test:
   *
   * 1. `scrollIntoView` tucks the guide beneath Impact's sticky header, so
   *    the offset is measured from the document instead.
   * 2. Entering compare cuts the page from 25 rows to three, and the browser
   *    clamps the offset to the new, shorter document AFTER layout. A SMOOTH
   *    scroll issued before that clamp is simply cancelled by it — which is
   *    what left the guide 2,100px above the viewport. An instant scroll
   *    survives, and this is a change of view, where a jump is honest.
   * 3. `requestAnimationFrame` does not fire in a backgrounded tab, so
   *    deferring the whole thing to a frame meant it silently never ran.
   *    The re-assert is on a timer for that reason.
   */
  applyScroll() {
    const what = this.scrollAfter;
    this.scrollAfter = null;
    if (!what || typeof window === 'undefined' || !window.scrollTo) return;

    this.scrollNow(what);
    // Once more after the layout settles, in case the clamp moved us. Same
    // target, so it is a no-op whenever the first attempt already landed.
    if (typeof setTimeout === 'function') setTimeout(() => this.scrollNow(what), 60);
  }

  scrollNow(what) {
    if (typeof window === 'undefined' || !window.scrollTo) return;

    if (what === 'restore') {
      window.scrollTo({ top: this.scrollBeforeCompare || 0, behavior: 'auto' });
      return;
    }

    // The COMPARE VIEW's top, not the guide's. Landing on the guide put the
    // title, the collection dropdown and both pill rows on screen — about
    // 200px of controls that mean nothing while you are comparing — and
    // pushed the numbers below the fold. Falls back to the element when the
    // compare block is not rendered.
    const target = this.querySelector('.ucsg__compare') || this;
    const rect = target.getBoundingClientRect ? target.getBoundingClientRect() : null;
    if (!rect) return;
    const top = rect.top + this.scrollY() - STICKY_HEADER_ALLOWANCE;
    window.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
  }
}

if (!customElements.get('uc-size-guide')) {
  customElements.define('uc-size-guide', UcSizeGuide);
}


})();
